import { useEffect, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import Logo from './Logo'
import QuickSignup from './QuickSignup'
import '@fontsource/anton/400.css'
import '@fontsource/cinzel/700.css'
import '@fontsource/great-vibes/400.css'
import './hero.css'

type Persona = { id: string; name: string; specialty: string; before: string; after: string }

// Fictional people, photos made with Codex. Photos carry no text: every line here is drawn in code.
const personas: Persona[] = [
  { id: 'brown', name: 'Sofia Ramos', specialty: 'Founder of the Year', before: 'No brand. No business. No board seat.', after: 'Owns 5 brands, 0 doubts.' },
  { id: 'man', name: 'Alex Taylor', specialty: 'Internet Expert', before: 'No certificate. No aura. No Rolex.', after: 'Boardroom energy unlocked.' },
  { id: 'purple', name: 'Juno Marlow', specialty: 'Creative Icon', before: 'No platform. No VIP list.', after: 'Creative-icon energy unlocked.' },
  { id: 'pink', name: 'Lena Park', specialty: 'Main Character', before: 'No spotlight. No board seat.', after: 'Main-character energy unlocked.' },
]

const ROTATE_MS = 4200

export default function HeroCarousel() {
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)
  const p = personas[active]

  useEffect(() => {
    if (paused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const timer = window.setInterval(() => setActive(i => (i + 1) % personas.length), ROTATE_MS)
    return () => window.clearInterval(timer)
  }, [paused])

  return (
    <section className="hx" aria-labelledby="hero-title">
      <div className="hx-top">
        <a className="hx-mark hx-badge" href="/" aria-label="LARPup home"><Logo size={60} /></a>
        <a className="hx-logo" href="/">LARP<i>up</i></a>
        <a className="hx-top-cta" href="#beta">Claim beta seat <ArrowRight size={15} /></a>
      </div>
      <div className="hx-giant" aria-hidden="true">CERTIFIED</div>

      <div className="hx-stage" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
        <figure className="hx-card hx-before">
          <div className="hx-photo">{personas.map((item, i) => <img key={item.id} src={`/hero/${item.id}-before.webp`} alt="" className={i === active ? 'on' : ''} decoding="async" />)}</div>
          <span className="hx-pill">Before</span>
          <figcaption key={`b-${p.id}`}>{p.before}</figcaption>
        </figure>

        <figure className="hx-card hx-after">
          <div className="hx-photo">{personas.map((item, i) => <img key={item.id} src={`/hero/${item.id}-after.webp`} alt="" className={i === active ? 'on' : ''} decoding="async" />)}</div>
          <span className="hx-pill hx-pill-after">After</span>
          <figcaption key={`a-${p.id}`}>{p.after}</figcaption>
        </figure>

        <div className="hx-cert" role="img" aria-label={`Sample certificate: ${p.name}, Certified Professional LARPer`}>
          <div className="hx-cert-inner" key={p.id}>
            <span className="hx-cert-brand">LARPup</span>
            <span className="hx-cert-kicker">Certified professional LARPer</span>
            <span className="hx-cert-name">{p.name}</span>
            <span className="hx-cert-spec">{p.specialty}</span>
            <span className="hx-cert-seal" aria-hidden="true" />
          </div>
        </div>
      </div>

      <div className="hx-dots" role="group" aria-label="Pick a before and after story">
        {personas.map((item, i) => <button key={item.id} type="button" aria-label={`${item.name}, ${item.specialty}`} aria-pressed={i === active} onClick={() => { setActive(i); setPaused(true) }} />)}
      </div>

      <div className="hx-copy">
        <span className="hx-label">LARP certification agency</span>
        <div className="hx-copy-row">
          <h1 id="hero-title"><span>Get LARP</span> <span>certified.</span></h1>
          <div className="hx-sub">
            <p>Become a professional LARPer.<br /><strong>Stop waiting, start winning.</strong></p>
            <QuickSignup preview={import.meta.env.DEV} />
            <a className="hx-btn" href="#make-card">Get my certificate <ArrowRight size={17} /></a>
          </div>
        </div>
      </div>

      <a className="hx-case" href="#beta">
        <img src="/hero/game-thumb.webp" alt="" loading="lazy" decoding="async" />
        <span><small>Coming to Steam</small><b>LARPup:<br />The Game</b><span className="hx-case-line">Only certified LARPers get in. Claim your beta seat before launch.</span><em>Claim beta seat</em></span>
      </a>
    </section>
  )
}
